// ============================================================
// AuditSetup.jsx
// Step 1: Collect company/audit header information
// ============================================================

import React, { useState } from "react";

export default function AuditSetup({ auditInfo, onComplete }) {
  const [form, setForm] = useState(auditInfo);
  const [errors, setErrors] = useState({});

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const validate = () => {
    const e = {};
    if (!form.companyName.trim()) e.companyName = "Company name is required.";
    if (!form.auditorName.trim()) e.auditorName = "Auditor name is required.";
    if (!form.auditDate) e.auditDate = "Audit date is required.";
    return e;
  };

  const handleSubmit = () => {
    const e = validate();
    if (Object.keys(e).length > 0) { setErrors(e); return; }
    onComplete(form);
  };

  return (
    <div className="page-container">
      <div className="page-header">
        <h1 className="page-title">Audit Setup</h1>
        <p className="page-desc">
          Enter facility and auditor information. This data will appear on all reports.
          Fields marked <span className="required-star">*</span> are required.
        </p>
      </div>

      <div className="card">
        <h2 className="card-title">Facility Information</h2>
        <div className="form-grid">
          <div className="form-group">
            <label className="form-label">Company Name <span className="required-star">*</span></label>
            <input
              className={`form-input ${errors.companyName ? "input-error" : ""}`}
              name="companyName"
              value={form.companyName}
              onChange={handleChange}
              placeholder="e.g., Acme Manufacturing Co."
            />
            {errors.companyName && <span className="error-msg">{errors.companyName}</span>}
          </div>

          <div className="form-group">
            <label className="form-label">Facility / Site Name</label>
            <input
              className="form-input"
              name="facilityName"
              value={form.facilityName}
              onChange={handleChange}
              placeholder="e.g., Main Plant – Building A"
            />
          </div>

          <div className="form-group full-width">
            <label className="form-label">Facility Address</label>
            <input
              className="form-input"
              name="facilityAddress"
              value={form.facilityAddress}
              onChange={handleChange}
              placeholder="Street, City, State, ZIP"
            />
          </div>

          <div className="form-group">
            <label className="form-label">Industry / SIC Code</label>
            <input
              className="form-input"
              name="industry"
              value={form.industry}
              onChange={handleChange}
              placeholder="e.g., Metal Fabrication / SIC 3440"
            />
          </div>

          <div className="form-group">
            <label className="form-label">Number of Employees</label>
            <input
              className="form-input"
              name="employeeCount"
              value={form.employeeCount}
              onChange={handleChange}
              placeholder="e.g., 85"
              type="number"
              min="1"
            />
          </div>
        </div>
      </div>

      <div className="card">
        <h2 className="card-title">Audit Information</h2>
        <div className="form-grid">
          <div className="form-group">
            <label className="form-label">Auditor Name <span className="required-star">*</span></label>
            <input
              className={`form-input ${errors.auditorName ? "input-error" : ""}`}
              name="auditorName"
              value={form.auditorName}
              onChange={handleChange}
              placeholder="Lead auditor full name"
            />
            {errors.auditorName && <span className="error-msg">{errors.auditorName}</span>}
          </div>

          <div className="form-group">
            <label className="form-label">Audit Type</label>
            <select
              className="form-input"
              name="auditType"
              value={form.auditType}
              onChange={handleChange}
            >
              <option>Comprehensive</option>
              <option>Targeted / Program-Specific</option>
              <option>Follow-Up</option>
              <option>Pre-Inspection</option>
              <option>Annual</option>
            </select>
          </div>

          <div className="form-group">
            <label className="form-label">Audit Date <span className="required-star">*</span></label>
            <input
              className={`form-input ${errors.auditDate ? "input-error" : ""}`}
              name="auditDate"
              type="date"
              value={form.auditDate}
              onChange={handleChange}
            />
            {errors.auditDate && <span className="error-msg">{errors.auditDate}</span>}
          </div>

          <div className="form-group">
            <label className="form-label">Next Scheduled Audit Date</label>
            <input
              className="form-input"
              name="nextAuditDate"
              type="date"
              value={form.nextAuditDate}
              onChange={handleChange}
            />
          </div>

          <div className="form-group full-width">
            <label className="form-label">Audit Scope / Notes</label>
            <textarea
              className="form-input form-textarea"
              name="scope"
              value={form.scope}
              onChange={handleChange}
              placeholder="Describe the scope, limitations, or specific areas covered in this audit..."
              rows={3}
            />
          </div>
        </div>
      </div>

      <div className="btn-row">
        <button className="btn btn-primary btn-lg" onClick={handleSubmit}>
          Continue to Scope Selection →
        </button>
      </div>
    </div>
  );
}
